import { writable, derived } from 'svelte/store';
import { loadChatHistory, addMessage, clearHistory } from '../utils/storage.js';
import { createChatAPI } from '../utils/api.js';
import { formatContent } from '../utils/contentFormatter.js';

// Current endpoint ID
export const currentEndpoint = writable(null);

// Messages for the current endpoint
export const messages = writable([]);

// Loading state
export const isLoading = writable(false);

// Error state
export const error = writable(null);

// Current suggestions
export const suggestions = writable([]);

// Chat API instance
export const chatAPI = derived(currentEndpoint, ($currentEndpoint) => {
    return $currentEndpoint ? createChatAPI($currentEndpoint) : null;
});

// Initialize chat for a specific endpoint
export function initializeChat(endpointId) {
    if (!endpointId) {
        currentEndpoint.set(null);
        messages.set([]);
        return;
    }
    
    currentEndpoint.set(endpointId);
    
    // Load existing messages
    const history = loadChatHistory(endpointId);
    messages.set(history);
    
    // Clear any previous errors
    error.set(null);
}

// Send a message
export async function sendMessage(messageText) {
    if (!messageText.trim()) return;
    
    const endpointId = getCurrentEndpoint();
    if (!endpointId) {
        error.set('No endpoint selected');
        return;
    }
    
    try {
        isLoading.set(true);
        error.set(null);
        
        // Add user message
        const userMessage = addMessage(endpointId, 'user', messageText.trim());
        messages.update(msgs => [...msgs, userMessage]);
        
        // Send to API
        const api = createChatAPI(endpointId);
        const response = await api.sendMessage(messageText.trim());
        console.log('ChatStore received response:', response);
        
        // Add AI response with content validation using formatter
        const rawContent = response.answer || 'No response received';
        const aiContent = formatContent(rawContent);
        
        console.log('AI Content to be saved:', aiContent);
        console.log('AI Content type:', typeof aiContent);
        console.log('AI Content length:', aiContent.length);
        
        const aiMessage = addMessage(endpointId, 'ai', aiContent);
        console.log('Created AI message:', aiMessage);
        messages.update(msgs => [...msgs, aiMessage]);
        
        // Update suggestions
        console.log('Setting suggestions:', response.suggestions);
        suggestions.set(response.suggestions || []);
        
    } catch (err) {
        console.error('Error sending message:', err);
        error.set(err.message || 'Failed to send message');
        
        // Add error message to chat
        const errorMessage = addMessage(endpointId, 'ai', `Error: ${err.message}`);
        messages.update(msgs => [...msgs, errorMessage]);
        
    } finally {
        isLoading.set(false);
    }
}

// Clear chat history
export function clearChat() {
    const endpointId = getCurrentEndpoint();
    if (!endpointId) return;
    
    clearHistory(endpointId);
    messages.set([]);
    suggestions.set([]);
    error.set(null);
}

// Get current endpoint ID
function getCurrentEndpoint() {
    let endpointId;
    currentEndpoint.subscribe(value => {
        endpointId = value;
    })();
    return endpointId;
}

// Retry last message
export async function retryLastMessage() {
    const currentMessages = getCurrentMessages();
    if (currentMessages.length === 0) return;
    
    // Find the last user message
    const lastUserMessage = [...currentMessages].reverse().find(msg => msg.type === 'user');
    if (!lastUserMessage) return;
    
    await sendMessage(lastUserMessage.content);
}

// Get current messages
function getCurrentMessages() {
    let currentMessages;
    messages.subscribe(value => {
        currentMessages = value;
    })();
    return currentMessages || [];
}

// Subscribe to route changes
if (typeof window !== 'undefined') {
    window.addEventListener('routechange', (event) => {
        const { endpointId } = event.detail;
        initializeChat(endpointId);
    });
}
